import { useWorkspace } from '@/stores/workspace.store';
import { useUi } from '@/stores/ui.store';

type TeamMember = {
  name: string;
  email: string;
  role: string;
  added: string;
};

const TEAM_MEMBERS: TeamMember[] = [
  { name: 'Sarah Chen', email: 'sarah@acme.com', role: 'Admin', added: 'Mar 1, 2026' },
  { name: 'Mike Johnson', email: 'mike@acme.com', role: 'Editor', added: 'Mar 15, 2026' },
];

const POSTING_PLATFORMS = ['LinkedIn', 'X / Twitter', 'Facebook', 'Instagram'];

export default function SettingsRoute() {
  const projectName = useWorkspace((s) => s.project_name) ?? 'Acme Corp';
  const toast = useUi((s) => s.toast);

  return (
    <div className="p-8 space-y-8">
      <h1 className="text-2xl font-semibold text-stone-900">Settings</h1>

      <div>
        <h2 className="text-base font-semibold text-stone-900 mb-4">Project settings</h2>
        <div className="bg-white border border-stone-200 rounded-xl p-5 space-y-5">
          <div>
            <label className="block text-sm text-stone-600 mb-2">Project name</label>
            <input
              type="text"
              defaultValue={projectName}
              className="w-full px-4 py-2.5 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          <div>
            <label className="block text-sm text-stone-600 mb-2">Brand voice description</label>
            <textarea
              className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
              rows={3}
              placeholder="Describe your brand's tone and style..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-stone-600 mb-2">Default timezone</label>
              <select className="w-full px-4 py-2.5 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500">
                <option>America/New_York (EST)</option>
                <option>America/Los_Angeles (PST)</option>
                <option>Europe/London (GMT)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-stone-600 mb-2">Language</label>
              <select className="w-full px-4 py-2.5 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500">
                <option>English</option>
                <option>Spanish</option>
                <option>French</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-base font-semibold text-stone-900 mb-4">Default posting times</h2>
        <div className="bg-white border border-stone-200 rounded-xl p-5">
          <div className="grid grid-cols-2 gap-4">
            {POSTING_PLATFORMS.map((platform) => (
              <div key={platform} className="flex items-center gap-4">
                <span className="w-28 text-sm text-stone-600">{platform}</span>
                <input
                  type="time"
                  defaultValue="09:00"
                  className="px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
                <input
                  type="time"
                  defaultValue="14:00"
                  className="px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-base font-semibold text-stone-900 mb-4">Team members</h2>
        <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-200">
                {['Member', 'Role', 'Added', ''].map((h) => (
                  <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-stone-500 uppercase">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {TEAM_MEMBERS.map((member) => (
                <tr key={member.email} className="border-b border-stone-100">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center font-medium text-sm">
                        {member.name.split(' ').map((n) => n[0]).join('')}
                      </div>
                      <div>
                        <div className="font-medium text-stone-900">{member.name}</div>
                        <div className="text-sm text-stone-500">{member.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <select
                      defaultValue={member.role}
                      className="px-3 py-1.5 border border-stone-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                    >
                      <option>Admin</option>
                      <option>Editor</option>
                      <option>Viewer</option>
                    </select>
                  </td>
                  <td className="px-5 py-4 text-sm text-stone-500">{member.added}</td>
                  <td className="px-5 py-4">
                    <button
                      onClick={() => toast(`Removed ${member.name}`)}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-5 py-3 bg-stone-50 border-t border-stone-200">
            <button
              onClick={() => toast('Invite sent')}
              className="text-sm text-violet-600 font-medium hover:text-violet-700"
            >
              + Invite team member
            </button>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => toast('Settings saved')}
          className="px-6 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium"
        >
          Save changes
        </button>
      </div>
    </div>
  );
}
